import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { adminFetchContent, adminSaveContentBatch, adminChangePassword } from '@/api/client';
import { useContent } from '@/contexts/ContentContext';

const DEFAULTS: Record<string, string> = {
  company_name: '',
  company_phone: '',
  company_email: '',
  business_address: '',
  facebook_url: '',
  instagram_url: '',
  yelp_url: '',
  weekday_hours: '',
  weekend_hours: '',
};

const FIELD_KEYS = [
  'company_name',
  'company_phone',
  'company_email',
  'business_address',
  'facebook_url',
  'instagram_url',
  'yelp_url',
  'weekday_hours',
  'weekend_hours',
];

export default function AdminSettings() {
  const { t } = useTranslation('home');
  const { refresh } = useContent();
  const [settings, setSettings] = useState<Record<string, string>>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Password fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const items = await adminFetchContent('en');
      const map: Record<string, string> = {};
      for (const item of items) {
        if (item.content_key && item.content_value !== undefined) {
          map[item.content_key] = item.content_value;
        }
      }
      setSettings(map);
    } catch {
      Swal.fire({
        title: t('admin_error'),
        text: t('admin_load_error'),
        icon: 'error',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleChange = (key: string, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);

    const items = FIELD_KEYS.map((key) => ({
      content_key: key,
      content_value: settings[key] || '',
    }));

    try {
      const result = await adminSaveContentBatch({ lang: 'en', items });

      if (result.success) {
        refresh();
        Swal.fire({
          title: t('admin_settings_saved'),
          text: t('admin_settings_saved'),
          icon: 'success',
          confirmButtonColor: '#3DBFA0',
          timer: 2000,
          timerProgressBar: true,
        });
      } else {
        Swal.fire({
          title: t('admin_error'),
          text: result.message || t('admin_save_error'),
          icon: 'error',
          confirmButtonColor: '#3DBFA0',
          confirmButtonText: t('admin_ok'),
        });
      }
    } catch {
      Swal.fire({
        title: t('admin_error'),
        text: t('admin_save_error'),
        icon: 'error',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    Swal.fire({
      title: t('admin_reset_defaults'),
      text: t('admin_reset_confirm'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#1A2E4A',
      confirmButtonText: t('admin_reset_defaults'),
      cancelButtonText: t('admin_cancel'),
    }).then(async (result) => {
      if (result.isConfirmed) {
        setSaving(true);
        const items = FIELD_KEYS.map((key) => ({
          content_key: key,
          content_value: '',
        }));
        try {
          const saveRes = await adminSaveContentBatch({ lang: 'en', items });
          if (saveRes.success) {
            setSettings({ ...DEFAULTS });
            Swal.fire({
              title: t('admin_reset_complete'),
              text: t('admin_reset_complete'),
              icon: 'success',
              confirmButtonColor: '#3DBFA0',
              timer: 1500,
            });
          } else {
            Swal.fire({
              title: t('admin_error'),
              text: saveRes.message || t('admin_save_error'),
              icon: 'error',
              confirmButtonColor: '#3DBFA0',
              confirmButtonText: t('admin_ok'),
            });
          }
        } catch {
          Swal.fire({
            title: t('admin_error'),
            text: t('admin_save_error'),
            icon: 'error',
            confirmButtonColor: '#3DBFA0',
            confirmButtonText: t('admin_ok'),
          });
        } finally {
          setSaving(false);
        }
      }
    });
  };

  const handleChangePassword = async () => {
    if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      Swal.fire({
        title: t('admin_error'),
        text: t('admin_password_required'),
        icon: 'warning',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
      return;
    }
    if (newPassword.length < 8) {
      Swal.fire({
        title: t('admin_error'),
        text: t('admin_password_min_error'),
        icon: 'warning',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      Swal.fire({
        title: t('admin_error'),
        text: t('admin_password_match_error'),
        icon: 'warning',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
      return;
    }

    setPwSaving(true);
    try {
      const result = await adminChangePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });

      if (result.success) {
        Swal.fire({
          title: t('admin_password_success'),
          icon: 'success',
          confirmButtonColor: '#3DBFA0',
          timer: 2000,
          timerProgressBar: true,
        });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const msg = (result.message || '').toLowerCase();
        const is404 = msg.includes('404') || msg.includes('not available');
        const isPasswordError = msg.includes('current password') || msg.includes('password');
        Swal.fire({
          title: t('admin_error'),
          text: is404
            ? t('admin_password_endpoint_error')
            : (result.message || t('admin_save_error')),
          icon: 'error',
          confirmButtonColor: '#3DBFA0',
          confirmButtonText: t('admin_ok'),
        });
      }
    } catch {
      Swal.fire({
        title: t('admin_error'),
        text: t('admin_password_endpoint_error'),
        icon: 'error',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
    } finally {
      setPwSaving(false);
    }
  };

  const sections = [
    {
      title: t('admin_business_info'),
      fields: [
        { key: 'company_name', label: t('admin_company_name'), type: 'text', placeholder: t('admin_placeholder_company') },
        { key: 'company_phone', label: t('admin_phone_number'), type: 'tel', placeholder: t('admin_placeholder_phone') },
        { key: 'company_email', label: t('admin_email_address'), type: 'email', placeholder: t('admin_placeholder_email') },
        { key: 'business_address', label: t('admin_business_address'), type: 'text', placeholder: t('admin_placeholder_address') },
      ],
    },
    {
      title: t('admin_social_media'),
      fields: [
        { key: 'facebook_url', label: t('admin_facebook_url'), type: 'url', placeholder: t('admin_placeholder_fb_url') },
        { key: 'instagram_url', label: t('admin_instagram_url'), type: 'url', placeholder: t('admin_placeholder_ig_url') },
        { key: 'yelp_url', label: t('admin_yelp_url'), type: 'url', placeholder: t('admin_placeholder_yelp_url') },
      ],
    },
    {
      title: t('admin_business_hours'),
      fields: [
        { key: 'weekday_hours', label: t('admin_weekday_hours'), type: 'text', placeholder: t('admin_placeholder_weekday') },
        { key: 'weekend_hours', label: t('admin_weekend_hours'), type: 'text', placeholder: t('admin_placeholder_weekend') },
      ],
    },
  ];

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-xl font-bold text-brand-dark font-heading">{t('admin_settings')}</h2>
        <p className="text-sm text-brand-muted mt-1">{t('admin_settings_tip')}</p>
      </div>

      {loading && (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
          <i className="ri-loader-4-line animate-spin text-brand-mint text-2xl" />
          <p className="text-sm text-brand-muted mt-3">{t('admin_loading')}</p>
        </div>
      )}

      {!loading && (
        <>
          {saving && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
              <div className="bg-white rounded-xl border border-gray-100 px-6 py-4 flex items-center gap-3">
                <i className="ri-loader-4-line animate-spin text-brand-mint text-lg" />
                <p className="text-sm font-medium text-brand-dark">{t('admin_saving')}</p>
              </div>
            </div>
          )}

          <div className="space-y-5">
            {sections.map((section) => (
              <div key={section.title} className="bg-white rounded-xl border border-gray-100 p-6">
                <h3 className="text-sm font-bold text-brand-dark font-heading uppercase tracking-wider mb-4">{section.title}</h3>
                <div className="space-y-4">
                  {section.fields.map((field) => (
                    <div key={field.key}>
                      <label className="block text-sm font-medium text-brand-dark mb-1.5">{field.label}</label>
                      <input
                        type={field.type}
                        value={settings[field.key] || ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                        placeholder={field.placeholder}
                        disabled={saving}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Admin Account — Change Password */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-brand-dark font-heading uppercase tracking-wider mb-4">{t('admin_change_password')}</h3>
              <div className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-brand-dark mb-1.5">{t('admin_current_password')}</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    placeholder={t('admin_current_password')}
                    disabled={pwSaving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-dark mb-1.5">{t('admin_new_password')}</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    placeholder={t('admin_new_password')}
                    disabled={pwSaving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-dark mb-1.5">{t('admin_confirm_password')}</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    placeholder={t('admin_confirm_password')}
                    disabled={pwSaving}
                  />
                </div>
                <button
                  onClick={handleChangePassword}
                  disabled={pwSaving}
                  className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap bg-brand-dark text-white hover:bg-brand-dark/90 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {pwSaving ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      {t('admin_saving')}
                    </>
                  ) : (
                    <>
                      <i className="ri-lock-password-line" />
                      {t('admin_password_update')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap bg-brand-mint text-white hover:bg-brand-mint/90 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  {t('admin_saving')}
                </>
              ) : (
                <>
                  <i className="ri-save-line" />
                  {t('admin_save_changes')}
                </>
              )}
            </button>
            <button
              onClick={handleReset}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-brand-dark text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              <i className="ri-refresh-line" />
              {t('admin_reset_defaults')}
            </button>
          </div>
        </>
      )}

      <div className="bg-blue-50/80 border border-blue-200 rounded-xl p-5 flex items-start gap-3">
        <i className="ri-lightbulb-line text-blue-600 text-lg mt-0.5 shrink-0" />
        <div>
          <h4 className="text-sm font-semibold text-blue-800">{t('admin_tip')}</h4>
          <p className="text-sm text-blue-700 mt-1">{t('admin_settings_tip')}</p>
        </div>
      </div>
    </div>
  );
}